import { useEffect, useRef } from 'react'
import { type Refs, type Screen, track, trackNow } from './events.ts'
import { createScreenStats, maskId, scrollDepth } from './screenStats.ts'

/**
 * What a teacher did on a screen, from one hook call per screen: it says the screen was viewed, and
 * when she leaves (navigates, or the tab is hidden) how long it was visible, how far down she
 * scrolled, what she pressed, repeated presses on one thing, and how many of its items came into
 * view. The browser owns all of it (ENGINEERING_RULES 22): the listeners live here, write into plain
 * variables, and never touch React state, so a scroll or a press cannot re-render anything.
 *
 * What she pressed is read from the control's own `id` (every interactive element has a unique one),
 * with record ids masked; items are the elements a page marks `data-screen-item`. No text of hers is
 * read anywhere.
 */

export type ScreenOptions = {
	/** The things on the screen, once known: kept as they are at the moment of each report, so a late answer still lands. */
	refs?: Refs
	/** How many items (questions, words, steps) the screen holds, when the page counts them itself. */
	items?: number
	/** False tracks nothing: a hook cannot be called conditionally. */
	enabled?: boolean
}

/** The screen on show, for what else wants to say where it happened (vitals). One owner: this module. */
let current: Screen | null = null
export const currentScreen = (): Screen | null => current

let first: Screen | null = null
const firstListeners: ((screen: Screen) => void)[] = []
/** Runs `cb` with the first screen of this page load, before that screen is reported; at once if it already happened. */
export function onFirstScreen(cb: (screen: Screen) => void): void {
	if (first) cb(first)
	else firstListeners.push(cb)
}
const noteFirst = (screen: Screen) => {
	if (first) return
	first = screen
	for (const cb of firstListeners.splice(0)) cb(screen)
}

/** What counts as a control worth naming: the nearest of these around the thing she pressed. */
const CONTROL =
	'button, a, input, select, textarea, summary, label, [role="button"], [role="tab"], [role="menuitem"], [role="checkbox"], [role="switch"], [role="radio"], [role="option"]'
const ITEM = '[data-screen-item]'
const SCAN_MS = 300

export function useScreen(screen: Screen, opts: ScreenOptions = {}): void {
	const latest = useRef(opts)
	useEffect(() => {
		latest.current = opts
	})
	const enabled = opts.enabled ?? true

	useEffect(() => {
		if (!enabled) return
		return watchScreen(screen, () => latest.current)
	}, [screen, enabled])
}

function watchScreen(screen: Screen, options: () => ScreenOptions): () => void {
	const stats = createScreenStats(Date.now, document.visibilityState !== 'hidden')
	current = screen
	noteFirst(screen)
	const o = options()
	const refsNow = () => options().refs
	track(
		{ kind: 'screen_viewed', data: { screen, ...(o.items ? { items: o.items } : {}) } },
		refsNow(),
	)

	const measure = () =>
		stats.scrolled(
			scrollDepth(window.scrollY, window.innerHeight, document.documentElement.scrollHeight),
		)
	const report = (how: 'navigated' | 'hidden') => {
		if (!stats.active()) return
		if (how === 'hidden') measure()
		const r = stats.take()
		const event = { kind: 'screen_left', data: { screen, how, ...r } } as const
		// Leaving the page rides the beacon now; moving on inside the app joins the next batch.
		if (how === 'hidden') trackNow(event, refsNow())
		else track(event, refsNow())
	}

	const onClick = (e: MouseEvent) => {
		// Leaving inside the app follows a click, and by then the page has its content: measure here,
		// not on a timer that can see a loading spinner that fits the screen and call it read.
		measure()
		const target = e.target instanceof Element ? e.target : null
		const control = target?.closest(CONTROL) ?? null
		stats.press(control ?? target, control?.id ? maskId(control.id) : null)
	}
	let scrollAt = 0
	const onScroll = () => {
		const t = Date.now()
		if (t - scrollAt < 100) return
		scrollAt = t
		measure()
	}
	// Stop the clock before reporting: the moment between a report and a later stop counted as a
	// fresh millisecond of viewing, and the pagehide that follows a hidden tab sent a second report.
	const onVisibility = () => {
		if (document.visibilityState === 'hidden') {
			stats.hide()
			report('hidden')
		} else stats.show()
	}
	const onPageHide = () => {
		stats.hide()
		report('hidden')
	}
	document.addEventListener('click', onClick, { capture: true, passive: true })
	window.addEventListener('scroll', onScroll, { passive: true })
	document.addEventListener('visibilitychange', onVisibility)
	window.addEventListener('pagehide', onPageHide)

	// Items come into view as she scrolls: each marked element is watched once, however late it renders.
	const watching = new WeakSet<Element>()
	let total = 0
	const io =
		typeof IntersectionObserver === 'function'
			? new IntersectionObserver(
					(entries) => {
						for (const entry of entries) {
							if (!entry.isIntersecting) continue
							stats.saw(entry.target)
							io?.unobserve(entry.target)
						}
					},
					{ threshold: 0.5 },
				)
			: null
	let scan: ReturnType<typeof setTimeout> | undefined
	const find = () => {
		scan = undefined
		for (const el of document.querySelectorAll(ITEM)) {
			if (watching.has(el)) continue
			watching.add(el)
			total += 1
			io?.observe(el)
		}
		stats.items(total)
	}
	// One scan per quiet moment, however much the page changes: the cost does not grow with renders.
	const mo =
		typeof MutationObserver === 'function'
			? new MutationObserver(() => {
					scan ??= setTimeout(find, SCAN_MS)
				})
			: null
	mo?.observe(document.body, { childList: true, subtree: true })
	find()
	if (o.items) stats.items(o.items)

	return () => {
		clearTimeout(scan)
		mo?.disconnect()
		io?.disconnect()
		document.removeEventListener('click', onClick, { capture: true })
		window.removeEventListener('scroll', onScroll)
		document.removeEventListener('visibilitychange', onVisibility)
		window.removeEventListener('pagehide', onPageHide)
		stats.items(options().items ?? 0)
		report('navigated')
		if (current === screen) current = null
	}
}
